"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import ParticleNumber from "@/app/components/landing/ParticleNumber";

/**
 * Lo que la Argentina lleva gastado en hormiga este mes.
 *
 * Modelo (defensible, conservador):
 *   - Adultos AR (18+): ~33M (INDEC 2022 + proyección)
 *   - Que consumen no esenciales con regularidad: ~26M (≈79%)
 *   - Promedio mensual de "hormiga + impulso": $12.000 ARS
 *
 * Total mensual ≈ 26M × $12.000 = $312.000.000.000 ARS
 * El número visible se interpola por minutos transcurridos del mes.
 */

const ADULTOS_NO_ESENCIALES = 26_000_000;
const HORMIGA_PROMEDIO_MENSUAL = 12_000; // ARS
const TOTAL_MENSUAL = ADULTOS_NO_ESENCIALES * HORMIGA_PROMEDIO_MENSUAL;
// Mes base: 1 mayo 2026 a las 00:00 (zona AR aproximada)
const MES_BASE_TIMESTAMP = new Date(2026, 4, 1, 0, 0, 0).getTime();
const MS_IN_MONTH = 30 * 24 * 60 * 60 * 1000;

function computeMonthHormigaSoFar(): number {
  const now = Date.now();
  const elapsedMs = Math.max(now - MES_BASE_TIMESTAMP, 0);
  const ratio = Math.min(elapsedMs / MS_IN_MONTH, 1);
  return Math.floor(ratio * TOTAL_MENSUAL);
}

function formatARS(n: number): string {
  return n.toLocaleString("es-AR");
}

export default function LiveCounterSection() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: false, amount: 0.4 });
  const seenOnceRef = useRef(false);
  const [hasBeenSeen, setHasBeenSeen] = useState(false);
  // Start at 0 so SSR and first client render match. Real value is set
  // after mount in useEffect below, avoiding hydration mismatch.
  const [value, setValue] = useState<number>(0);
  const [initialValue, setInitialValue] = useState<number>(0);

  useEffect(() => {
    const v = computeMonthHormigaSoFar();
    setInitialValue(v);
    setValue(v);
  }, []);

  useEffect(() => {
    if (inView && !seenOnceRef.current && initialValue > 0) {
      seenOnceRef.current = true;
      setHasBeenSeen(true);
    }
  }, [inView, initialValue]);

  useEffect(() => {
    if (!inView || initialValue === 0) return;
    const id = window.setInterval(() => {
      setValue(computeMonthHormigaSoFar());
    }, 1000);
    return () => window.clearInterval(id);
  }, [inView, initialValue]);

  return (
    <section
      ref={ref}
      data-section="section-counter"
      className="relative py-24 sm:py-32 border-y border-white/[0.05]"
      aria-label="Lo que los argentinos llevan gastado en hormiga"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <motion.div
          data-particle-shield
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="text-[12px] tracking-[0.2em] uppercase text-white/40 mb-6">
            En este mes · estimado en vivo
          </div>
          <p className="text-[clamp(28px,5vw,52px)] font-medium leading-[1.18] text-white/85 mb-10 max-w-3xl tracking-tight">
            Los argentinos llevamos gastados{" "}
            <ParticleNumber
              text={`$${formatARS(initialValue)}`}
              liveText={`$${formatARS(value)}`}
              active={hasBeenSeen}
              className="font-extrabold whitespace-nowrap"
              gradient
            />{" "}
            en cafés, kioscos, delivery y consumos chiquitos que ya no vamos a
            recordar mañana.
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/40">
            <span>~26M de adultos · ~$12.000 por persona / mes</span>
            <span className="hidden sm:inline w-1 h-1 rounded-full bg-white/20" />
            <span>Estimación basada en datos de consumo de INDEC</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
